import { getConfig } from '@superbet-group/web.lib.config';
import Vue, {
    AsyncComponent,
    VueConstructor,
} from 'vue';
import {
    delay,
    Logger,
} from '@superbet-group/web.lib.utilities';

import { logger as defaultLogger } from '@app/logger';
import { defineAsyncComponent } from '@vue/composition-api';
import { SpinnerContainer } from '@superbet-group/web.lib.ui-components';

export type ComponentWithProps<Props> = VueConstructor<Vue & Props>;
export type ComponentWithPropsExport<Props> = { default: ComponentWithProps<Props> } | ComponentWithProps<Props>;

const retriesForLoadingFederationComponent = 3;

export function loadAsyncComponent<Props>(
    importer: () => Promise<ComponentWithPropsExport<Props>>,
    syncFallback: ComponentWithProps<Props>,
    logger: Logger = defaultLogger,
): AsyncComponent {
    const importerWithErrorHandling = async (retryCount = 0): Promise<ComponentWithPropsExport<Props>> => {
        const isNonProd = !getConfig().environment.isProdEnvironment;

        try {
            return await importer();
        } catch (error) {
            if (retryCount < retriesForLoadingFederationComponent) {
                if (isNonProd) {
                    console.debug('Failed to load async component, retrying');
                }

                await delay(200);
                return importerWithErrorHandling(retryCount + 1);
            }

            logger.error(error as Error);

            if (isNonProd) {
                console.debug('Failed to load async component, falling back');
            }

            return syncFallback;
        }
    };

    return defineAsyncComponent({
        loader: () => importerWithErrorHandling(),
        delay: 50,
    });
}

export function loadAsyncWithLoadingState<Props>(
    importer: () => Promise<ComponentWithPropsExport<Props>>,
) {
    return defineAsyncComponent({
        loader: importer,
        loadingComponent: SpinnerContainer,
        delay: 50,
    });
}
